import type { VerticalProfileImagery, VerticalProfileImagerySubject, VerticalProfileWritePayload } from '../types/vertical-profile.types';

export const EMPTY_IMAGERY_SUBJECT: VerticalProfileImagerySubject = {
  title: '',
  description: '',
  tags: [],
};

export const EMPTY_IMAGERY: VerticalProfileImagery = {
  required: 'recommended',
  precision: 50,
  precision_label: 'Moderate',
  subjects: [],
  fidelity_notes: '',
  avoid_images: [],
  shooting_guidance: '',
  ai_image_notes: '',
};

function trimCoerced(value: unknown): string {
  return String(value ?? '').trim();
}

export function trimStringList(values: string[]): string[] {
  return values.map((value) => trimCoerced(value)).filter(Boolean);
}

export function buildVerticalProfilePayload(values: VerticalProfileWritePayload): VerticalProfileWritePayload {
  return {
    ...values,
    name: values.name?.trim(),
    profile_id: values.profile_id?.trim(),
    group: values.group?.trim(),
    tone_guidance: values.tone_guidance?.trim(),
    copy_prompt: values.copy_prompt?.trim(),
    change_note: values.change_note?.trim(),
    industries: values.industries ? trimStringList(values.industries) : undefined,
    media_types: values.media_types ? trimStringList(values.media_types) : undefined,
    purposes: values.purposes ? trimStringList(values.purposes) : undefined,
    required_elements: values.required_elements ? trimStringList(values.required_elements) : undefined,
    required_roles: values.required_roles ? trimStringList(values.required_roles) : undefined,
    key_visual_rules: values.key_visual_rules ? trimStringList(values.key_visual_rules) : undefined,
    avoidances: values.avoidances ? trimStringList(values.avoidances) : undefined,
    imagery: values.imagery
      ? {
          ...values.imagery,
          precision_label: values.imagery.precision_label?.trim() ?? '',
          fidelity_notes: values.imagery.fidelity_notes?.trim() ?? '',
          shooting_guidance: values.imagery.shooting_guidance?.trim() ?? '',
          ai_image_notes: values.imagery.ai_image_notes?.trim() ?? '',
          avoid_images: trimStringList(values.imagery.avoid_images ?? []),
          subjects: (values.imagery.subjects ?? []).map((subject) => ({
            title: subject.title.trim(),
            description: subject.description.trim(),
            tags: trimStringList(subject.tags),
          })).filter((subject) => subject.title || subject.description),
        }
      : undefined,
  };
}

export function validateVerticalProfileForm(
  values: VerticalProfileWritePayload,
  options: { isCreate: boolean },
): string | null {
  if (!values.name?.trim()) {
    return 'Name is required.';
  }
  if (options.isCreate && !values.profile_id?.trim()) {
    return 'Profile ID is required when creating a profile.';
  }
  if (!values.group?.trim()) {
    return 'Group is required.';
  }
  if (!values.tone_guidance?.trim()) {
    return 'Tone guidance is required.';
  }
  if (!values.copy_prompt?.trim()) {
    return 'Copy prompt is required.';
  }
  if (values.imagery) {
    const precision = values.imagery.precision;
    if (precision < 0 || precision > 100) {
      return 'Imagery precision must be between 0 and 100.';
    }
  }
  return null;
}

export function parseVerticalProfileMutationError(error: unknown): string {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return 'Could not save vertical profile. You may need staff or admin access.';
  }
  const data = (error as { data?: Record<string, unknown> }).data;
  if (!data) {
    return 'Could not save vertical profile.';
  }
  if (typeof data.detail === 'string') {
    return data.detail;
  }
  const messages = Object.entries(data)
    .map(([field, value]) => {
      if (Array.isArray(value)) {
        return `${field}: ${value.join(', ')}`;
      }
      return `${field}: ${String(value)}`;
    });
  return messages.length > 0 ? messages.join(' ') : 'Could not save vertical profile.';
}
